import type { ReactNode } from "react";

import {
  CalendarDays,
  CheckCircle,
  // DatabaseBackup,
  FileClock,
  FileStack,
  LayoutDashboard,
  MessagesSquare,
  Search,
  Truck,
  Users,
} from "lucide-react";

import { IconeMenuNotificacoes } from "@components/notificacoes/icone-menu-notificacoes";

import { resolveUserProfile } from "@/common/navigation/user-profile";
import { BACKOFFICE_URL } from "@/environments";
import { FEATURES, type FeatureKey, hasFeature } from "@/utils/auth/features";

export type SidebarMenuItem = {
  id: number;
  title: string;
  link: string;
  icon: ReactNode;
  subRoutes?: string[];
  external?: boolean;
  /** Link externo do backoffice: abre já logado com o usuário do portal (código de uso único). */
  abrirComSessao?: boolean;
  /** Item só aparece no menu quando o usuário tem esta feature habilitada. */
  requiredFeature: FeatureKey;
};

const iconClass = "size-4 text-white duration-500";

const backofficeMenuItem: SidebarMenuItem = {
  id: 99,
  title: "Backoffice",
  link: BACKOFFICE_URL,
  icon: <LayoutDashboard className={iconClass} />,
  external: true,
  abrirComSessao: true,
  requiredFeature: FEATURES.ACESSAR_BACKOFFICE,
};

export const menusClient: SidebarMenuItem[] = [
  {
    id: 3,
    title: "Modelos",
    link: "/",
    icon: <CheckCircle className={iconClass} />,
    subRoutes: ["/", "/details-model"],
    requiredFeature: FEATURES.ACESSAR_MODELOS_CLIENTE,
  },
  {
    id: 5,
    title: "Remessas Andamento",
    link: "/shipments-in-progress",
    icon: <FileClock className={iconClass} />,
    subRoutes: ["/shipments-in-progress", "/details-in-progress"],
    requiredFeature: FEATURES.ACESSAR_REMESSAS_ANDAMENTO,
  },
  {
    id: 4,
    title: "Histórico de Remessas",
    link: "/shipping-history",
    icon: <FileStack className={iconClass} />,
    requiredFeature: FEATURES.ACESSAR_HISTORICO_REMESSAS,
  },
  {
    id: 6,
    title: "Conversa",
    link: "/minha-conversa",
    icon: <MessagesSquare className={iconClass} />,
    requiredFeature: FEATURES.ACESSAR_CONVERSA_CLIENTE,
  },
  {
    id: 8,
    title: "Agenda",
    link: "/minha-agenda",
    icon: <CalendarDays className={iconClass} />,
    requiredFeature: FEATURES.ACESSAR_AGENDA_CLIENTE,
  },
  {
    id: 7,
    title: "Notificações",
    link: "/notificacoes",
    icon: <IconeMenuNotificacoes className={iconClass} />,
    requiredFeature: FEATURES.ACESSAR_NOTIFICACOES,
  },
];

export const menusAdmin: SidebarMenuItem[] = [
  {
    id: 3,
    title: "Remessas",
    link: "/",
    icon: <CheckCircle className={iconClass} />,
    requiredFeature: FEATURES.ACESSAR_REMESSAS,
  },
  {
    id: 2,
    title: "Pesquisar remessa",
    link: "/search-shipment",
    icon: <Search className={iconClass} />,
    subRoutes: ["/search-shipment", "/visualizar-modelo"],
    requiredFeature: FEATURES.ACESSAR_PESQUISAR_REMESSAS,
  },
  {
    id: 4,
    title: "Expedição",
    link: "/expedition",
    icon: <Truck className={iconClass} />,
    requiredFeature: FEATURES.ACESSAR_EXPEDICAO,
  },
  {
    id: 5,
    title: "Recepção",
    link: "/reception",
    icon: <CheckCircle className={iconClass} />,
    requiredFeature: FEATURES.ACESSAR_RECEPCAO,
  },
  {
    id: 1,
    title: "Gerenciar clientes",
    link: "/manage-clients",
    icon: <Users className={iconClass} />,
    subRoutes: [
      "/manage-clients",
      "/details-client",
      "/my-data",
      "/manage-client-users",
      "/manage-technical-sheets",
      "/edit-client-data",
    ],
    requiredFeature: FEATURES.ACESSAR_GERENCIAR_CLIENTE,
  },
  backofficeMenuItem,
];

export const menusProducao: SidebarMenuItem[] = [
  {
    id: 1,
    title: "Remessas",
    link: "/",
    icon: <CheckCircle className={iconClass} />,
    subRoutes: ["/", "/visualizar-modelo"],
    requiredFeature: FEATURES.ACESSAR_REMESSAS,
  },
  {
    id: 2,
    title: "Pesquisar remessa",
    link: "/search-shipment",
    icon: <Search className={iconClass} />,
    requiredFeature: FEATURES.ACESSAR_PESQUISAR_REMESSAS,
  },
  {
    id: 3,
    title: "Gerenciar clientes",
    link: "/manage-clients",
    icon: <Users className={iconClass} />,
    subRoutes: [
      "/manage-clients",
      "/details-client",
      "/my-data",
      "/manage-client-users",
      "/manage-technical-sheets",
      "/edit-client-data",
    ],
    requiredFeature: FEATURES.ACESSAR_GERENCIAR_CLIENTE,
  },
  backofficeMenuItem,
];

export const menusConsultor: SidebarMenuItem[] = [
  {
    id: 2,
    title: "Pesquisar remessa",
    link: "/",
    icon: <Search className={iconClass} />,
    requiredFeature: FEATURES.ACESSAR_PESQUISAR_REMESSAS,
  },
  {
    id: 3,
    title: "Gerenciar clientes",
    link: "/manage-clients",
    icon: <Users className={iconClass} />,
    subRoutes: [
      "/manage-clients",
      "/details-client",
      "/my-data",
      "/manage-client-users",
      "/edit-client-data",
    ],
    requiredFeature: FEATURES.ACESSAR_GERENCIAR_CLIENTE,
  },
];

export const menusExpedicao: SidebarMenuItem[] = [
  {
    id: 1,
    title: "Expedir Remessas",
    link: "/",
    icon: <Truck className={iconClass} />,
    subRoutes: ["/", "/visualizar-modelo"],
    requiredFeature: FEATURES.ACESSAR_EXPEDICAO,
  },
  {
    id: 2,
    title: "Pesquisar remessa",
    link: "/search-shipment",
    icon: <Search className={iconClass} />,
    requiredFeature: FEATURES.ACESSAR_PESQUISAR_REMESSAS,
  },
];

export const menusRecepcao: SidebarMenuItem[] = [
  {
    id: 1,
    title: "Finalizar Remessas",
    link: "/",
    icon: <CheckCircle className={iconClass} />,
    subRoutes: ["/", "/visualizar-modelo"],
    requiredFeature: FEATURES.ACESSAR_RECEPCAO,
  },
  {
    id: 2,
    title: "Pesquisar remessa",
    link: "/search-shipment",
    icon: <Search className={iconClass} />,
    requiredFeature: FEATURES.ACESSAR_PESQUISAR_REMESSAS,
  },
];

export function getMenusByFeatures(
  features: readonly string[] | null | undefined
): SidebarMenuItem[] {
  const profile = resolveUserProfile(features);

  const baseMenu = (() => {
    switch (profile) {
      case "admin":
        return menusAdmin;
      case "consultor":
        return menusConsultor;
      case "cliente":
        return menusClient;
      case "producao":
        return menusProducao;
      case "expedicao":
        return menusExpedicao;
      case "recepcao":
        return menusRecepcao;
      default:
        return [];
    }
  })();

  return baseMenu.filter((menu) => {
    if (menu.external && !menu.link) return false;

    return hasFeature(features, menu.requiredFeature);
  });
}

export function splitMenusByType(menus: SidebarMenuItem[]) {
  return {
    internalMenus: menus.filter((menu) => !menu.external),
    externalMenus: menus.filter((menu) => menu.external),
  };
}
